import React, { useState, useEffect, useCallback } from 'react';
import { HiOutlinePaperAirplane, HiOutlineRefresh, HiOutlineEye, HiOutlineDownload } from 'react-icons/hi';
import axiosInstance from '../../../api/axiosConfig';

/**
 * The match-review table shown after a payroll PDF is split.
 *
 * This is the safety gate of the whole module: payroll data cannot be unsent, so
 * an operator confirms every employee-code -> employee match here before anything
 * is dispatched. Rows that cannot be sent are shown with the reason rather than
 * quietly dropped.
 */

const MATCH_LABELS = {
    matched: { text: 'Ready', className: 'pd-pill-ok' },
    no_master_record: { text: 'Not in master', className: 'pd-pill-err' },
    inactive: { text: 'Inactive', className: 'pd-pill-warn' },
    invalid_mobile: { text: 'No mobile', className: 'pd-pill-err' },
    already_sent: { text: 'Already sent', className: 'pd-pill-muted' }
};

const STATUS_LABELS = {
    pending: { text: 'Pending', className: 'pd-pill-muted' },
    skipped: { text: 'Skipped', className: 'pd-pill-muted' },
    sent: { text: 'Sent', className: 'pd-pill-ok' },
    delivered: { text: 'Delivered', className: 'pd-pill-ok' },
    read: { text: 'Read', className: 'pd-pill-ok' },
    failed: { text: 'Failed', className: 'pd-pill-err' }
};

const Pill = ({ map, value }) => {
    const meta = map[value] || { text: value, className: 'pd-pill-muted' };
    return <span className={`pd-pill ${meta.className}`}>{meta.text}</span>;
};

const SUITABILITY = {
    ready: { label: 'Recommended', className: 'pd-pill-ok' },
    caution: { label: 'Usable — check warning', className: 'pd-pill-warn' },
    blocked: { label: 'Cannot be used', className: 'pd-pill-err' }
};

const BatchReview = ({ batch, onRefresh, canSend, gatewayReady }) => {
    const [sending, setSending] = useState(false);
    const [message, setMessage] = useState(null);

    // Templates live on the WhatsApp gateway, so they are fetched rather than
    // hardcoded — a template approved minutes ago is selectable straight away.
    const [templates, setTemplates] = useState([]);
    const [templateName, setTemplateName] = useState(batch?.templateName || '');
    const [loadingTemplates, setLoadingTemplates] = useState(true);
    const [templateError, setTemplateError] = useState(null);
    const [syncing, setSyncing] = useState(false);

    const loadTemplates = useCallback(async () => {
        setLoadingTemplates(true);
        setTemplateError(null);
        try {
            const res = await axiosInstance.get('/api/payslips/templates');
            const list = res.data?.data || [];
            setTemplates(list);

            // Keep the batch's own choice if it is still valid, otherwise fall back
            // to the .env default, then to the best available candidate.
            setTemplateName((current) => {
                const stillValid = list.find((t) => t.name === current && t.suitability !== 'blocked');
                if (stillValid) return current;
                const preferred = res.data?.meta?.defaultTemplate;
                if (preferred && list.some((t) => t.name === preferred && t.suitability !== 'blocked')) return preferred;
                const best = list.find((t) => t.suitability !== 'blocked');
                return best ? best.name : '';
            });
        } catch (err) {
            setTemplateError(err.response?.data?.message || 'Could not load WhatsApp templates');
        } finally {
            setLoadingTemplates(false);
        }
    }, []);

    useEffect(() => { loadTemplates(); }, [loadTemplates]);

    const syncFromMeta = async () => {
        setSyncing(true);
        setTemplateError(null);
        try {
            const res = await axiosInstance.post('/api/payslips/templates/sync');
            setMessage({ type: 'ok', text: res.data?.message || 'Templates synced' });
            await loadTemplates();
        } catch (err) {
            setTemplateError(err.response?.data?.message || 'Sync failed');
        } finally {
            setSyncing(false);
        }
    };

    if (!batch) return null;

    const selected = templates.find((t) => t.name === templateName) || null;
    const entries = batch.entries || [];
    const sendable = entries.filter((e) => e.matchStatus === 'matched' && ['pending', 'failed'].includes(e.status));
    const failed = entries.filter((e) => e.status === 'failed');
    const blocked = entries.filter((e) => e.matchStatus !== 'matched');

    const post = async (url, confirmText, successText) => {
        if (!window.confirm(confirmText)) return;
        setSending(true);
        setMessage(null);
        try {
            const res = await axiosInstance.post(url, { templateName });
            setMessage({ type: 'ok', text: res.data?.message || successText });
            // Dispatch runs in the background; poll so progress appears without a reload.
            setTimeout(onRefresh, 1500);
        } catch (error) {
            setMessage({
                type: 'err',
                text: error.response?.data?.message || 'Request failed'
            });
        } finally {
            setSending(false);
        }
    };

    const handleSend = () =>
        post(
            `/api/payslips/batches/${batch._id}/send`,
            `Send ${sendable.length} payslip(s) for ${batch.payrollMonthLabel || batch.payrollMonth} over WhatsApp` +
                `\n\nTemplate: ${templateName}` +
                (selected?.suitability === 'caution' ? `\n\nWARNING: ${selected.reason}` : '') +
                '\n\nThis cannot be undone.',
            'Dispatch started'
        );

    const handleRetry = () =>
        post(
            `/api/payslips/batches/${batch._id}/retry-failed`,
            `Retry ${failed.length} failed payslip(s)?`,
            'Retry started'
        );

    const openPdf = (entry) => {
        // Opened via axiosInstance so the JWT is attached, then handed to the
        // browser as a blob — a bare window.open would hit the route unauthenticated.
        axiosInstance
            .get(`/api/payslips/batches/${batch._id}/entries/${entry._id}/pdf`, { responseType: 'blob' })
            .then((res) => {
                const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                window.open(url, '_blank', 'noopener');
                setTimeout(() => URL.revokeObjectURL(url), 60000);
            })
            .catch(() => setMessage({ type: 'err', text: 'Could not open that payslip' }));
    };

    return (
        <div className="pd-card">
            <div className="pd-card-head">
                <div>
                    <h3>{batch.originalFilename}</h3>
                    <p className="pd-muted">
                        Payroll period <strong>{batch.payrollMonthLabel || batch.payrollMonth}</strong> ·{' '}
                        {batch.totals?.payslips} payslip(s) from {batch.totals?.pages} page(s)
                    </p>
                </div>
                <div className="pd-actions">
                    <button className="pd-btn pd-btn-ghost" onClick={onRefresh} disabled={sending}>
                        <HiOutlineRefresh size={16} /> Refresh
                    </button>
                    {failed.length > 0 && canSend && (
                        <button className="pd-btn pd-btn-warn" onClick={handleRetry} disabled={sending}>
                            <HiOutlineRefresh size={16} /> Retry {failed.length} failed
                        </button>
                    )}
                    {sendable.length > 0 && (
                        <button
                            className="pd-btn pd-btn-primary"
                            onClick={handleSend}
                            disabled={sending || !canSend || !gatewayReady || !templateName || batch.status === 'sending'}
                            title={
                                !canSend
                                    ? 'Sending requires the "approve" permission'
                                    : !gatewayReady
                                    ? 'WhatsApp gateway is not configured'
                                    : !templateName
                                    ? 'Choose a WhatsApp template first'
                                    : ''
                            }
                        >
                            <HiOutlinePaperAirplane size={16} /> Send {sendable.length} payslip(s)
                        </button>
                    )}
                </div>
            </div>

            {/* Template picker — read live from the gateway, never hardcoded. */}
            <div className="pd-template-picker">
                <div className="pd-field">
                    <label>WhatsApp template</label>
                    <div className="pd-template-row">
                        <select
                            value={templateName}
                            onChange={(e) => setTemplateName(e.target.value)}
                            disabled={loadingTemplates || sending}
                        >
                            {loadingTemplates && <option value="">Loading templates…</option>}
                            {!loadingTemplates && templates.length === 0 && <option value="">No templates found</option>}
                            {!loadingTemplates &&
                                templates.map((t) => (
                                    <option key={t.id || t.name} value={t.name} disabled={t.suitability === 'blocked'}>
                                        {t.suitability === 'blocked' ? '✕ ' : t.suitability === 'caution' ? '! ' : '✓ '}
                                        {t.name} — {t.category}/{t.headerType}
                                        {t.paramCount ? ` · ${t.paramCount} param(s)` : ''}
                                        {t.suitability === 'blocked' ? ' (unusable)' : ''}
                                    </option>
                                ))}
                        </select>
                        <button className="pd-btn pd-btn-ghost" onClick={loadTemplates} disabled={loadingTemplates || syncing}>
                            <HiOutlineRefresh size={16} /> Reload
                        </button>
                        {canSend && (
                            <button
                                className="pd-btn pd-btn-ghost"
                                onClick={syncFromMeta}
                                disabled={syncing || loadingTemplates}
                                title="Pull newly-created templates down from Meta"
                            >
                                <HiOutlineDownload size={16} /> {syncing ? 'Syncing…' : 'Sync from Meta'}
                            </button>
                        )}
                    </div>
                    {selected && (
                        <div className="pd-template-meta">
                            <span className={`pd-pill ${SUITABILITY[selected.suitability].className}`}>
                                {SUITABILITY[selected.suitability].label}
                            </span>
                            {selected.bodyPreview && <span className="pd-muted">{selected.bodyPreview}</span>}
                        </div>
                    )}
                </div>
            </div>

            {templateError && <div className="pd-banner pd-banner-err">{templateError}</div>}

            {selected?.suitability === 'caution' && (
                <div className="pd-banner pd-banner-warn">
                    <div>
                        <strong>“{selected.name}” is a {selected.category} template.</strong> {selected.reason}
                    </div>
                </div>
            )}

            {!loadingTemplates && templates.length > 0 && !templates.some((t) => t.suitability === 'ready') && (
                <div className="pd-banner pd-banner-info">
                    No ideal template exists yet. Create one in Autowaba with <strong>category UTILITY</strong> and a{' '}
                    <strong>DOCUMENT header</strong>, then use “Sync from Meta”.
                </div>
            )}

            <div className="pd-stats">
                <div className="pd-stat"><span>{batch.totals?.matched || 0}</span>Ready</div>
                <div className="pd-stat"><span>{batch.totals?.sent || 0}</span>Sent</div>
                <div className="pd-stat pd-stat-err"><span>{batch.totals?.failed || 0}</span>Failed</div>
                <div className="pd-stat pd-stat-warn"><span>{batch.totals?.unmatched || 0}</span>Not sendable</div>
            </div>

            {batch.status === 'sending' && (
                <div className="pd-banner pd-banner-info">
                    Sending in progress — this page refreshes as messages go out.
                </div>
            )}

            {message && (
                <div className={`pd-banner ${message.type === 'ok' ? 'pd-banner-ok' : 'pd-banner-err'}`}>
                    {message.text}
                </div>
            )}

            {blocked.length > 0 && (
                <div className="pd-banner pd-banner-warn">
                    <strong>{blocked.length} payslip(s) cannot be sent.</strong> Add the missing employees to the
                    master table (or fix their mobile numbers) and upload again.
                </div>
            )}

            <div className="pd-table-wrap">
                <table className="pd-table">
                    <thead>
                        <tr>
                            <th>Code</th>
                            <th>Name on slip</th>
                            <th>Matched employee</th>
                            <th>Mobile</th>
                            <th>Net</th>
                            <th>Page</th>
                            <th>Match</th>
                            <th>Delivery</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map((entry) => (
                            <tr key={entry._id} className={entry.matchStatus !== 'matched' ? 'pd-row-muted' : ''}>
                                <td className="pd-mono">{entry.employeeCode}</td>
                                <td>{entry.employeeNameOnSlip || '—'}</td>
                                <td>
                                    {entry.matchedName || <span className="pd-muted">—</span>}
                                    {entry.matchNote && <div className="pd-note">{entry.matchNote}</div>}
                                </td>
                                <td className="pd-mono">{entry.mobileMasked || '—'}</td>
                                <td className="pd-num">{entry.netSalary || '—'}</td>
                                <td className="pd-num">{(entry.pageNumbers || []).join(', ')}</td>
                                <td><Pill map={MATCH_LABELS} value={entry.matchStatus} /></td>
                                <td>
                                    <Pill map={STATUS_LABELS} value={entry.status} />
                                    {entry.errorMessage && <div className="pd-note pd-note-err">{entry.errorMessage}</div>}
                                </td>
                                <td>
                                    <button className="pd-icon-btn" title="Preview this payslip" onClick={() => openPdf(entry)}>
                                        <HiOutlineEye size={16} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default BatchReview;
